'use client'

import { MapPosition } from '@/lib/map/types'

// Utility function to check if Google Maps is available
const isGoogleMapsAvailable = (): boolean => {
  return (
    typeof window !== 'undefined' &&
    window.google?.maps !== undefined &&
    typeof window.google.maps.Geocoder === 'function'
  )
}

export interface GeocodingResult {
  address: string
  components: {
    administrativeAreaLevel1?: string
    administrativeAreaLevel2?: string
    country?: string
    locality?: string
    postalCode?: string
    route?: string
    streetNumber?: string
  }
  formattedAddress: string
  placeId?: string
}

export class GeocodingService {
  private geocoder: google.maps.Geocoder | null = null

  constructor() {
    this.initializeGeocoder()
  }

  /**
   * Forward geocoding: Convert address to coordinates
   */
  async geocode(
    address: string
  ): Promise<{ position: MapPosition; result: GeocodingResult } | null> {
    if (!this.isReady()) {
      const isReady = await this.waitForReady()
      if (!isReady) {
        console.warn(
          'Geocoder not initialized. Make sure Google Maps API is loaded.'
        )
        return null
      }
    }

    try {
      const results = await new Promise<google.maps.GeocoderResult[]>(
        (resolve, reject) => {
          this.geocoder!.geocode({ address }, (results, status) => {
            if (status === 'OK' && results) {
              resolve(results)
            } else {
              reject(new Error(`Geocoding failed: ${status}`))
            }
          })
        }
      )

      if (results.length === 0) {
        return null
      }

      const result = results[0]
      const position: MapPosition = {
        lat: result.geometry.location.lat(),
        lng: result.geometry.location.lng()
      }

      return {
        position,
        result: this.parseGeocodingResult(result)
      }
    } catch (error) {
      console.error('Geocoding error:', error)
      return null
    }
  }

  /**
   * Get a short address for display (e.g., "123 Main St, City")
   */
  getShortAddress(result: GeocodingResult): string {
    const parts = []

    if (result.components.streetNumber) {
      parts.push(result.components.streetNumber)
    }
    if (result.components.route) {
      parts.push(result.components.route)
    }
    if (result.components.administrativeAreaLevel2) {
      parts.push(result.components.administrativeAreaLevel2)
    }
    if (result.components.administrativeAreaLevel1) {
      parts.push(result.components.administrativeAreaLevel1)
    }

    if (result.components.locality) {
      parts.push(result.components.locality)
    }

    return parts.join(', ') || result.address
  }

  /**
   * Check if geocoder is ready
   */
  isReady(): boolean {
    return this.geocoder !== null && isGoogleMapsAvailable()
  }

  /**
   * Reverse geocoding: Convert coordinates to address
   */
  async reverseGeocode(position: MapPosition): Promise<GeocodingResult | null> {
    if (!this.isReady()) {
      const isReady = await this.waitForReady()
      if (!isReady) {
        console.warn(
          'Geocoder not initialized. Make sure Google Maps API is loaded.'
        )
        return null
      }
    }

    try {
      const results = await new Promise<google.maps.GeocoderResult[]>(
        (resolve, reject) => {
          this.geocoder!.geocode({ location: position }, (results, status) => {
            if (status === 'OK' && results) {
              resolve(results)
            } else {
              reject(new Error(`Geocoding failed: ${status}`))
            }
          })
        }
      )

      if (results.length === 0) {
        return null
      }

      const result = results[0]
      return this.parseGeocodingResult(result)
    } catch (error) {
      console.error('Reverse geocoding error:', error)
      return null
    }
  }

  /**
   * Wait for geocoder to be ready
   */
  async waitForReady(timeout = 5000): Promise<boolean> {
    if (this.isReady()) {
      return true
    }

    return new Promise((resolve) => {
      const startTime = Date.now()
      const checkInterval = setInterval(() => {
        // Re-initialize if Google Maps becomes available
        if (isGoogleMapsAvailable() && !this.geocoder) {
          this.initializeGeocoder()
        }

        if (this.isReady()) {
          clearInterval(checkInterval)
          resolve(true)
        } else if (Date.now() - startTime > timeout) {
          clearInterval(checkInterval)
          resolve(false)
        }
      }, 100)
    })
  }

  private initializeGeocoder() {
    if (!isGoogleMapsAvailable()) {
      return
    }

    try {
      // Double-check that Geocoder is actually a constructor before instantiating
      if (typeof google.maps.Geocoder === 'function') {
        this.geocoder = new google.maps.Geocoder()
      }
    } catch (error) {
      console.warn('Failed to initialize Geocoder:', error)
      this.geocoder = null
    }
  }

  private parseGeocodingResult(
    result: google.maps.GeocoderResult
  ): GeocodingResult {
    const components: GeocodingResult['components'] = {}

    // Parse address components
    result.address_components.forEach((component) => {
      const types = component.types
      if (types.includes('street_number')) {
        components.streetNumber = component.long_name
      } else if (types.includes('route')) {
        components.route = component.long_name
      } else if (types.includes('locality')) {
        components.locality = component.long_name
      } else if (types.includes('administrative_area_level_1')) {
        components.administrativeAreaLevel1 = component.long_name
      } else if (types.includes('administrative_area_level_2')) {
        components.administrativeAreaLevel2 = component.long_name
      } else if (types.includes('country')) {
        components.country = component.long_name
      } else if (types.includes('postal_code')) {
        components.postalCode = component.long_name
      }
    })

    // Build a readable address
    const addressParts = []
    if (components.streetNumber && components.route) {
      addressParts.push(`${components.streetNumber} ${components.route}`)
    } else if (components.route) {
      addressParts.push(components.route)
    }

    if (components.locality) {
      addressParts.push(components.locality)
    }

    if (components.administrativeAreaLevel1) {
      addressParts.push(components.administrativeAreaLevel1)
    }

    if (components.country) {
      addressParts.push(components.country)
    }

    const address = addressParts.join(', ')

    return {
      address,
      components,
      formattedAddress: result.formatted_address,
      placeId: result.place_id
    }
  }
}

// Singleton instance
export const geocodingService = new GeocodingService()
